import { ReactNode, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';

import { cohortDay, etWhen } from './overviewModel';
import { useLifecycleToast } from './Toast';
import { C, cardStyle, Icon, PATHS, Plain, text14, Toggle } from './ui';
import { STEP_DOWN } from '../JourneysTab/EngineCard';
import GoLiveModal from '../JourneysTab/GoLiveModal';
import {
  budgetState,
  SEGMENT_COST_MISSING,
  segmentCostMissing,
} from '../JourneysTab/journeySummary';
import {
  BudgetForm,
  PilotForm,
  TaxGuideForm,
} from '../JourneysTab/PilotBudgetRows';
import {
  ReloadFailedError,
  RELOAD_FAILED_MESSAGE,
} from '../JourneysTab/useJourneys';
import { formatMoney } from '../shared/format';

import ConfirmationModal from '@/components/ConfirmationModalV2';
import useAuth from '@/hooks/useAuth';
import ROUTES from '@/routes/routes';
import {
  JourneyChanges,
  JourneysPayload,
  lifecycleErrorMessage,
} from '@/services/lifecycleAPI';

type Props = {
  data: JourneysPayload;
  onRefresh: () => Promise<void>;
  onSave: (changes: JourneyChanges) => Promise<void>;
};

const TENANT_ONLY = 'Tenant admin only';
const subStyle = { ...text14, color: C.grey } as const;
const titleStyle = { fontWeight: 700, fontSize: 16, lineHeight: 1.4 } as const;

const Row = ({
  title,
  sub,
  right,
  onClick,
  testId,
  disabledTitle,
}: {
  title: string;
  sub: ReactNode;
  right?: ReactNode;
  onClick?: () => void;
  testId: string;
  disabledTitle?: string;
}) => {
  const body = (
    <>
      <div
        style={{
          display: 'flex',
          flexDirection: 'column',
          gap: 4,
          minWidth: 0,
        }}
      >
        <span style={titleStyle}>{title}</span>
        <span style={subStyle}>{sub}</span>
      </div>
      {right}
    </>
  );
  const layout = {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    gap: 24,
    padding: '8px 0',
    width: '100%',
  } as const;
  return onClick ? (
    <Plain data-testid={testId} style={layout} type="button" onClick={onClick}>
      {body}
    </Plain>
  ) : (
    <div data-testid={testId} style={layout} title={disabledTitle}>
      {body}
    </div>
  );
};

const Chevron = ({ color = C.black }: { color?: string }) => (
  <Icon color={color} d={PATHS.chevronRight} />
);

const MessagingControls = ({ data, onRefresh, onSave }: Props) => {
  const { currentAdminIsTenantAdminRole: canEdit } = useAuth();
  const { toast } = useLifecycleToast();
  const navigate = useNavigate();
  const { global } = data;
  const [open, setOpen] = useState(true);
  const [dialog, setDialog] = useState<'pilot' | 'budget' | 'taxGuide' | null>(
    null,
  );
  const [confirm, setConfirm] = useState<'off' | 'live' | null>(null);
  const [saving, setSaving] = useState(false);
  const [goLiveError, setGoLiveError] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [refreshing, setRefreshing] = useState(false);

  // The toggle is the mode, which is what it saves. With the kill switch off
  // it read Off for a live mode and could only save live again, so messages
  // started the moment the switch came back; the subtitle says nothing is
  // sent while it is off (review D-02).
  const live = global.mode === 'live';

  // A save that went through but whose reload failed is still a success.
  const save = async (changes: JourneyChanges) => {
    try {
      await onSave(changes);
    } catch (err) {
      if (err instanceof ReloadFailedError) {
        setError(RELOAD_FAILED_MESSAGE);
        return;
      }
      throw err;
    }
  };

  // One save at a time: the off dialog has no busy state, so a double click
  // sent two saves with one version and the second came back as a 409 that
  // blamed another admin (review D-01). A ref, because both clicks of a
  // double click run before `saving` re-renders.
  const changing = useRef(false);
  const changeMode = async (mode: 'off' | 'live') => {
    if (changing.current) return;
    changing.current = true;
    setSaving(true);
    setError(null);
    setGoLiveError(null);
    try {
      await save({ mode });
      setConfirm(null);
      if (mode === 'off') {
        toast(
          'Automated messages paused',
          'No automated messages will go out until you turn this back on.',
        );
      } else if (!global.kill_switch_on) {
        toast(
          'Live mode saved',
          'The kill switch is off, so no messages are sent.',
        );
      } else {
        toast(
          'Automated messages resumed',
          'Journeys will start sending again.',
        );
      }
    } catch (err) {
      const message = lifecycleErrorMessage(err, 'Could not change the mode.');
      if (mode === 'live') setGoLiveError(message);
      else {
        setConfirm(null);
        setError(message);
      }
    } finally {
      changing.current = false;
      setSaving(false);
    }
  };

  const refresh = async () => {
    setRefreshing(true);
    setError(null);
    try {
      await onRefresh();
      toast('Data refreshed');
    } catch (err) {
      setError(lifecycleErrorMessage(err, 'Could not refresh the stats.'));
    } finally {
      setRefreshing(false);
    }
  };

  const autoSub = (() => {
    if (!global.kill_switch_on) {
      return 'The kill switch is off, so nothing is sent in any mode.';
    }
    if (global.mode === 'shadow') {
      return 'Shadow mode: messages are recorded, not sent.';
    }
    return 'Turning this off stops every automated message for all workers right away.';
  })();

  const budget = budgetState(global);
  const budgetSub = (() => {
    if (budget.kind === 'none') return 'SMS is off: no monthly budget is set.';
    if (budget.kind === 'zero') return 'SMS is off: budget is $0.';
    return (
      <>
        <b
          data-testid="budgetSpend"
          style={{ color: budget.kind === 'spent' ? C.red : C.green }}
        >
          {formatMoney(budget.spend)} / {formatMoney(budget.budget)}
        </b>{' '}
        this month
      </>
    );
  })();
  // With no segment cost every text costs $0, so "0% used" would promise a
  // cap that never applies (review RT-08).
  const costMissing = budget.kind === 'ok' && segmentCostMissing(global);
  const budgetRight = (() => {
    if (costMissing) return SEGMENT_COST_MISSING;
    if (budget.kind === 'ok') return `${budget.pct}% used`;
    if (budget.kind === 'spent') {
      return budget.overBy > 0
        ? `Over budget by ${formatMoney(budget.overBy)}: SMS is not sent`
        : 'Used up: SMS is not sent';
    }
    return '';
  })();
  const budgetRed =
    budget.kind === 'spent' || budget.kind === 'zero' || costMissing;

  return (
    <section
      aria-label="Messaging controls"
      data-testid="messagingControls"
      style={{ display: 'flex', flexDirection: 'column', gap: 12 }}
    >
      <Plain
        aria-expanded={open}
        data-testid="messagingControlsToggle"
        style={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          width: '100%',
        }}
        type="button"
        onClick={() => setOpen((v) => !v)}
      >
        <span style={{ fontWeight: 700, fontSize: 18, lineHeight: 1.4 }}>
          Messaging controls
        </span>
        <Icon
          d={PATHS.chevronUp}
          style={{
            transform: open ? 'none' : 'rotate(180deg)',
            transition: 'transform .15s',
          }}
        />
      </Plain>
      {open && (
        <div style={{ ...cardStyle, gap: 20 }}>
          <div
            style={{
              display: 'flex',
              flexDirection: 'column',
              gap: 8,
              paddingBottom: 20,
              borderBottom: `1px solid ${C.stroke}`,
            }}
          >
            <Row
              disabledTitle={canEdit ? undefined : TENANT_ONLY}
              sub={
                <>
                  Users who{' '}
                  <b>
                    {/* No date: every unfinished sign-up is in (#62, RT-13). */}
                    {cohortDay(global.cohort_start_on)
                      ? `signed up on or after ${cohortDay(global.cohort_start_on)},`
                      : 'signed up at any time (no cohort date),'}
                  </b>{' '}
                  Zip prefixes: <b>{global.cohort_zip_prefixes || 'Not set'}</b>
                </>
              }
              testId="pilotGroupRow"
              title="Pilot group"
              onClick={canEdit ? () => setDialog('pilot') : undefined}
            />
            <Row
              right={
                <span title={canEdit ? undefined : TENANT_ONLY}>
                  <Toggle
                    disabled={!canEdit || saving}
                    label="Automated messages"
                    on={live}
                    onClick={() => {
                      setError(null);
                      setGoLiveError(null);
                      setConfirm(live ? 'off' : 'live');
                    }}
                  />
                </span>
              }
              sub={autoSub}
              testId="automatedMessagesRow"
              title="Automated messages"
            />
            <Row
              disabledTitle={canEdit ? undefined : TENANT_ONLY}
              right={
                <span
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: 16,
                    ...text14,
                    color: budgetRed ? C.red : C.grey,
                    fontWeight: budgetRed ? 600 : 400,
                  }}
                >
                  <span data-testid="budgetUsed">{budgetRight}</span>
                  <Chevron />
                </span>
              }
              sub={budgetSub}
              testId="smsBudgetRow"
              title="SMS Budget"
              onClick={canEdit ? () => setDialog('budget') : undefined}
            />
            <Row
              disabledTitle={canEdit ? undefined : TENANT_ONLY}
              right={<Chevron />}
              sub={
                global.tax_guide_url ? (
                  <span style={{ overflowWrap: 'anywhere' }}>
                    {global.tax_guide_url}
                  </span>
                ) : (
                  <span style={{ color: C.amber }}>
                    Not set: J2 tax messages are skipped
                  </span>
                )
              }
              testId="taxGuideRow"
              title="Tax guide link"
              onClick={canEdit ? () => setDialog('taxGuide') : undefined}
            />
            <Row
              right={
                <span
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: 8,
                    ...text14,
                    color: C.grey,
                    flexShrink: 0,
                  }}
                >
                  <span>See workers</span>
                  <Chevron color={C.green} />
                </span>
              }
              sub={
                <>
                  <b
                    data-testid="waitingCount"
                    style={{
                      color: global.awaiting_review_count > 0 ? C.red : C.black,
                    }}
                  >
                    {global.awaiting_review_count.toLocaleString('en-US')}{' '}
                    workers
                  </b>{' '}
                  are blocked on our side: waiting on a video review, I-9 rep,
                  or E-Verify.
                </>
              }
              testId="waitingRow"
              title="Waiting on our team"
              onClick={() => navigate(`/${ROUTES.workersV2}`)}
            />
          </div>
          {global.heartbeat_stale && (
            <div
              data-testid="heartbeatStale"
              role="status"
              style={{ ...text14, color: C.amber, fontWeight: 600 }}
            >
              Engine not running: no sweep or send in the last 2 hours. Messages
              are not going out.
            </div>
          )}
          {error && (
            <div
              data-testid="engineError"
              role="alert"
              style={{ ...text14, color: C.red, fontWeight: 600 }}
            >
              {error}
            </div>
          )}
          <div
            style={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              ...text14,
            }}
          >
            <div style={{ display: 'flex', gap: 12, color: C.grey }}>
              <span>
                Last sweep <b>{etWhen(global.last_sweep_at)}</b>
              </span>
              <span>
                Last send <b>{etWhen(global.last_dispatch_at)}</b>
              </span>
            </div>
            {/* Named by its visible text first, so screen readers hear the
                time and voice control finds "Updated" (review D-11). */}
            <Plain
              aria-label={`Updated ${etWhen(global.stats_computed_at)} · refresh stats`}
              data-testid="statsRefreshBtn"
              disabled={refreshing}
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: 8,
                color: C.green,
              }}
              type="button"
              onClick={refresh}
            >
              <span>Updated {etWhen(global.stats_computed_at)}</span>
              <Icon color={C.green} d={PATHS.refresh} />
            </Plain>
          </div>
        </div>
      )}

      {dialog === 'pilot' && (
        <PilotForm
          global={global}
          onClose={() => setDialog(null)}
          onSave={save}
        />
      )}
      {dialog === 'budget' && (
        <BudgetForm
          global={global}
          onClose={() => setDialog(null)}
          onSave={save}
        />
      )}
      {dialog === 'taxGuide' && (
        <TaxGuideForm
          global={global}
          onClose={() => setDialog(null)}
          onSave={save}
        />
      )}
      {confirm === 'live' && (
        <GoLiveModal
          data={data}
          error={goLiveError}
          saving={saving}
          onCancel={() => setConfirm(null)}
          onConfirm={() => changeMode('live')}
        />
      )}
      {confirm === 'off' && (
        <ConfirmationModal
          disableClickout
          cancelLabel="Cancel"
          confirmLabel={STEP_DOWN.off.confirmLabel}
          message={STEP_DOWN.off.message}
          subMessage="Workers in the pilot stop getting messages."
          title={STEP_DOWN.off.title}
          onCancel={() => setConfirm(null)}
          onConfirm={() => changeMode('off')}
        />
      )}
    </section>
  );
};

export default MessagingControls;
